<script setup lang="ts">
import type { AffectedBuildingBlockSummary } from '@/components/calculation-form/utils/calculationDatabaseSwitch'
import { ISSUE_SEVERITIES, type IssueSeverity } from '@/constants/issueSeverity'

const open = defineModel<boolean>({ required: true })

const props = withDefaults(
  defineProps<{
    affectedBlocks: AffectedBuildingBlockSummary[]
    baseDatabaseName?: string
    targetDatabaseName?: string
  }>(),
  {
    baseDatabaseName: '',
    targetDatabaseName: ''
  }
)

const resolveSeverityLabel = (severity: IssueSeverity) => {
  return severity === ISSUE_SEVERITIES.MISSING ? 'Missing values' : 'Changed values'
}

const resolveSeverityColor = (severity: IssueSeverity) => {
  return severity === ISSUE_SEVERITIES.MISSING ? 'error' : 'warning'
}

const resolveMaterialSummary = (materialNames: string[]) => {
  if (!materialNames.length) return 'No material names available.'
  if (materialNames.length <= 3) return materialNames.join(', ')

  const preview = materialNames.slice(0, 3).join(', ')
  const remaining = materialNames.length - 3
  return `${preview} +${remaining} more`
}
</script>

<template>
  <v-dialog v-model="open" max-width="780" persistent>
    <v-card class="pa-3 pt-6">
      <v-card-title>Building blocks affected by database switch</v-card-title>
      <v-card-text>
        <p class="mb-2">
          The selected environmental database was applied, but one or more custom building blocks
          now have missing or changed default values.
        </p>
        <p class="text-medium-emphasis mb-4">
          Review details and fix affected rows in the Building Blocks tab.
        </p>

        <div v-if="baseDatabaseName || targetDatabaseName" class="text-body-2 mb-4">
          <strong>From:</strong> {{ baseDatabaseName || 'Unknown' }}
          <span class="mx-1">-></span>
          <strong>To:</strong> {{ targetDatabaseName || 'Unknown' }}
        </div>

        <v-sheet class="summary-table" rounded>
          <v-row class="summary-header px-3 py-2 ma-0">
            <v-col cols="5" class="text-body-2 font-weight-medium">Building block</v-col>
            <v-col cols="3" class="text-body-2 font-weight-medium">Issue</v-col>
            <v-col cols="4" class="text-body-2 font-weight-medium">Affected materials</v-col>
          </v-row>

          <v-divider />

          <v-row
            v-for="block in affectedBlocks"
            :key="block.blockId"
            class="summary-row px-3 py-2 ma-0"
          >
            <v-col cols="5" class="text-body-2">{{ block.blockName }}</v-col>
            <v-col cols="3" class="text-body-2">
              <v-chip :color="resolveSeverityColor(block.severity)" size="small" variant="tonal">
                {{ resolveSeverityLabel(block.severity) }}
              </v-chip>
            </v-col>
            <v-col cols="4" class="text-body-2 text-medium-emphasis">
              {{ resolveMaterialSummary(block.materialNames) }}
            </v-col>
          </v-row>
        </v-sheet>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" class="text-none" @click="open = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.summary-table {
  border: 1px solid #d4d4d4;
  background-color: rgb(var(--v-theme-surface));
}

.summary-header {
  background-color: #f5f7f9;
}

.summary-row + .summary-row {
  border-top: 1px solid #ececec;
}
</style>
